import { IHealthQuote } from "@/app/models/healthQuote";
import { IOffersResponse } from "@/@types/health/offers";
import { withConsentUrl } from '@/app/utils/consent-url'

const BASE_API = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/health`

// Offers go through the same-origin Next proxy so the httpOnly admin JWT is forwarded as Bearer; a direct cross-origin call is unauthenticated and Symfony strips the CRM exclusion reasons.
const OFFERS_PROXY = "/api/health/offers"

const ENDPOINTS = {
    VALIDATE_LANDING: `${BASE_API}/validate-landing`,
    GET_QUOTE_FROM_TOKEN: `${BASE_API}/token/%TOKEN%`,
    SIMULATION: `${BASE_API}/v2/simulation`,
    QUOTATION: `${BASE_API}/v2/devis`,
    PRE_SUBSCRIPTION: `${BASE_API}/v2/presubscription`,
    CALLBACK_MODAL_OPEN: `${BASE_API}/callback-modal-open`,
    CALLBACK: `${BASE_API}/callback`,
    CONSENT_PARTNERS: `${BASE_API}/consent-partners`,
    TRANSMIT_SWISS_BORDER_WORKER_LEAD: `${BASE_API}/transmit-swiss-border-worker-lead`,
    APIS: `${BASE_API}/apis`,
}

// Names of the health APIs currently switched on. null on failure so callers show every logo rather than none.
export async function fetchEnabledHealthApiNames(): Promise<string[] | null> {
    try {
        const response = await fetch(ENDPOINTS.APIS, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            next: { revalidate: 300 },
        })

        if (!response.ok) {
            return null
        }

        const data: { apis: { name: string; enabled: boolean }[] } = await response.json()

        return (data.apis ?? []).filter((api) => api.enabled).map((api) => api.name)
    } catch {
        return null
    }
}

export interface IConsentPartnerInfo {
    partner: string
    label: string
    orias: string | null
    // API the partner is reachable through; a lead broker answers with its own clients, all sharing its API name
    from_api?: string
}

export interface IConsentPartnersPayload {
    contractors: {
        type: string
        social_regime: number | null
        professional_activity: number | null
        birthday_date: string
    }[]
    children_count: number
    stars: { consultations: number; hospital_care: number; dental: number; optical: number }
}

// Partners that may contact the prospect, resolved from the birth date and the job alone so the funnel can name them from the zipcode step on; null = no list
export async function postConsentPartners(payload: IConsentPartnersPayload): Promise<IConsentPartnerInfo[] | null> {
    try {
        const response = await fetch(ENDPOINTS.CONSENT_PARTNERS, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            cache: "no-store",
            signal: AbortSignal.timeout(5000),
        })
        if (!response.ok) return null
        const data = await response.json().catch(() => null)
        return Array.isArray(data?.partners) ? data.partners : null
    } catch {
        return null
    }
}

export async function notifyCallbackModalOpen(data: {
    token: string
    rm_product_name: string
    offer_id: string
    offer_cost: number | null
}): Promise<void> {
    try {
        await fetch(ENDPOINTS.CALLBACK_MODAL_OPEN, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            cache: 'no-store',
        })
    } catch (error) {
        console.error('Error notifying callback modal open:', error)
    }
}

export async function getHealthQuoteFromToken(token: string): Promise<IHealthQuote> {
    if (token === 'test') {
        return {
            id: 123,
            token: 'test',
            contractors: [],
            ask_previous_contract_infos: false,
            offer_name: null,
            offer: null,
            effective_date: '',
            stars: { id: 1, formated_stars: { medkit: 0, stethoscope: 0, glasses: 0, tooth: 0 } },
            already_has_contract: false,
            insured_more_than_12_months: false,
            family_type: 1,
            offer_id: null,
            offer_cost: null,
            offer_company_name: null,
            rm_product_name: null,
            selected_options: null,
            is_recommended_offer: false,
            personas: '',
            composition_wording: '',
            need_wording: '',
            persona_category: '',
        } as IHealthQuote
    }

    const response = await fetch(
        ENDPOINTS.GET_QUOTE_FROM_TOKEN.replace('%TOKEN%', token),
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            cache: 'no-store',
        }
    )

    if (!response.ok) {
        throw new Error(`Error fetching quote: ${response.statusText}`)
    }

    return await response.json()
}

// Non-throwing variant: null when the quote no longer exists, so callers can fall back instead of erroring.
export async function getHealthQuoteFromTokenOrNull(token: string): Promise<IHealthQuote | null> {
    try {
        return await getHealthQuoteFromToken(token)
    } catch {
        return null
    }
}

export interface IValidateLandingPayload {
    abTestReviewsVersion: string | null
    token: string | null
    assignToExpert: string | null
    partnerSource: string | null
    originRoute: string
}

// Cross-site call: 'include' forwards the rm_* attribution cookies Symfony reads only when the quote is first persisted.
export async function postValidateLanding(data: IValidateLandingPayload): Promise<{ token?: string } | null> {
    const response = await fetch(ENDPOINTS.VALIDATE_LANDING, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include',
        cache: 'no-store',
    })

    return response.ok ? await response.json() : null
}

export async function postSimulation(data: any): Promise<any> {
    const response = await fetch(
        ENDPOINTS.SIMULATION,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(withConsentUrl(data)),
            credentials: 'include',
            cache: 'no-store',
        }
    )

    if (!response.ok) {
        throw new Error(`Error posting simulation: ${response.statusText}`)
    }

    return await response.json()
}

export async function postOffers(
    data: { token: string; stars: { medkit: number; stethoscope: number; glasses: number; tooth: number }; skip_disable_flag?: boolean; skip_needs_filters?: boolean }
): Promise<IOffersResponse> {
    const response = await fetch(OFFERS_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'same-origin',
        cache: 'no-store',
    })

    if (!response.ok) {
        throw new Error(`Error posting offers: ${response.statusText}`)
    }

    const offersResponse: IOffersResponse = await response.json()
    // Symfony renvoie offer_cost en string pour certains partenaires (ex: ilona_kiwi), on normalise en number
    return {
        ...offersResponse,
        offers: (offersResponse.offers ?? []).map((offer) => ({ ...offer, offer_cost: Number(offer.offer_cost) })),
    }
}

export async function postQuotation(data: any): Promise<any> {
    const response = await fetch(
        ENDPOINTS.QUOTATION,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(withConsentUrl(data)),
            credentials: 'include',
            cache: 'no-store',
        }
    )

    if (!response.ok) {
        throw new Error(`Error posting quotation: ${response.statusText}`)
    }

    return await response.json()
}

// Posts the full quote payload to Symfony's /api/health/callback after a
// successful /api/common/callback so the health workflow logs the action and
// triggers the partner pipeline.
export async function postHealthCallback(data: any): Promise<any> {
    const response = await fetch(ENDPOINTS.CALLBACK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include',
        cache: 'no-store',
    })

    if (!response.ok) {
        throw new Error(`Error posting health callback: ${response.statusText}`)
    }

    return await response.json()
}

// Mirrors HealthForm.vue:2908. Called when the user picks "Travailleur
// frontalier suisse" as their main profession — Symfony forwards the lead
// to the partner before the user is redirected to the partner's landing page.
// Fire-and-forget: the Vue side uses `.finally(...)` so a failure here must
// NOT block the subsequent redirect.
export async function transmitSwissBorderWorkerLead(token: string): Promise<void> {
    try {
        await fetch(ENDPOINTS.TRANSMIT_SWISS_BORDER_WORKER_LEAD, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
            cache: 'no-store',
        })
    } catch (error) {
        console.error('Error transmitting swiss-border-worker lead:', error)
    }
}

export async function postPreSubscription(data: any): Promise<any> {
    const response = await fetch(
        ENDPOINTS.PRE_SUBSCRIPTION,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
            credentials: 'include',
            cache: 'no-store',
        }
    )

    if (!response.ok) {
        throw new Error(`Error posting pre-subscription: ${response.statusText}`)
    }

    return await response.json()
}
